/** Service duration helpers for companyServices / order conflict windows */

export const TRAVEL_BUFFER_MINUTES = 30;

/** Default durations (minutes) by service category keywords. */
export const DEFAULT_SERVICE_DURATION_MINUTES = {
  battery: 30,
  oil: 30,
  wash: 45,
  care: 45,
  tire: 60,
  brake: 60,
  detailing: 120,
  polish: 120,
  fallback: 45,
} as const;

export function formatServiceDurationLabel(totalMinutes: number | null | undefined): string {
  const n = Math.max(0, Math.round(Number(totalMinutes) || 0));
  if (!n) {
    return '';
  }
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h > 0 && m > 0) {
    return `${h}h ${m}min`;
  }
  if (h > 0) {
    return h === 1 ? '1h' : `${h}h`;
  }
  return `${m} min`;
}

export function splitServiceDurationMinutes(totalMinutes: number | null | undefined): {
  hours: number;
  minutes: number;
} {
  const n = Math.max(0, Math.round(Number(totalMinutes) || 0));
  return {
    hours: Math.floor(n / 60),
    minutes: n % 60,
  };
}

export function combineServiceDurationMinutes(
  hours: number | null | undefined,
  minutes: number | null | undefined
): number {
  const h = Math.max(0, Math.floor(Number(hours) || 0));
  const m = Math.max(0, Math.min(59, Math.floor(Number(minutes) || 0)));
  return h * 60 + m;
}

function orderServiceText(order: any): string {
  const ds = order?._dynamicService;
  return [
    order?.serviceNameEn,
    order?.serviceName,
    order?.serviceNameAr,
    order?.category,
    order?.category?.catNameEn,
    order?.category?.catNameAr,
    ds?.serviceNameEn,
    ds?.serviceNameAr,
    ds?.category?.catNameEn,
    ds?.category?.catNameAr,
  ]
    .filter(Boolean)
    .map((v) => String(v))
    .join(' ')
    .toLowerCase();
}

/** Resolve default duration from service/category naming. */
export function getDefaultDurationMinutesForOrder(order: any): number {
  const text = orderServiceText(order);
  if (text.includes('detail') || text.includes('polish')) {
    return DEFAULT_SERVICE_DURATION_MINUTES.detailing;
  }
  if (text.includes('tire') || text.includes('tyre') || text.includes('brake')) {
    return DEFAULT_SERVICE_DURATION_MINUTES.tire;
  }
  if (text.includes('battery') || text.includes('oil')) {
    return DEFAULT_SERVICE_DURATION_MINUTES.battery;
  }
  if (text.includes('wash') || text.includes('care')) {
    return DEFAULT_SERVICE_DURATION_MINUTES.wash;
  }
  return DEFAULT_SERVICE_DURATION_MINUTES.fallback;
}

/**
 * Prefer explicit company/order duration, else category defaults.
 */
export function resolveServiceDurationMinutes(
  order: any,
  companyService?: any
): number {
  const candidates = [
    companyService?.serviceDurationMinutes,
    order?.serviceDurationMinutes,
    order?._companyService?.serviceDurationMinutes,
    order?._dynamicService?.serviceDurationMinutes,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return Math.round(n);
    }
  }
  return getDefaultDurationMinutesForOrder(order);
}
